/**
 * Invoice formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_office_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const invoiceFormatsPack: ToilePack = {
    slug: 'invoice-formats',
    category: 'office',
    name: 'Invoice formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'gst', label: 'GST invoice', subject: 'an invoice format with GSTIN, HSN code and tax columns'},
        {key: 'freelance', label: 'Freelance', subject: 'a freelance invoice with hours and rate'},
        {key: 'simple', label: 'Simple', subject: 'a simple invoice with a logo placeholder'},
    ],
    styles: [
        {key: 'grid', label: 'Grid', style: 'clean ruled invoice with thin black borders, bold column headings and a boxed total due', palette: 'black ink on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'modern', label: 'Modern', style: 'modern invoice with a large title, a coloured header row and a rounded total panel', palette: 'indigo and light grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'receipt', label: 'Ledger', style: 'traditional ledger style invoice with double rules and a shaded total row', palette: 'dark green and grey on off-white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
    ],
    directions: ['green header', 'dark header', 'with stamp'],
    directionHint: 'try: green header',
    seo: {
        title: 'Invoice formats: GST, freelance and simple',
        metaDescription: 'Nine printable invoice formats: a GST, a freelance and a simple invoice, each in a grid, modern or ledger style.',
        h1: 'Invoice format: GST, freelance, simple',
        tagline: 'Three invoice layouts, each in three styles, with a boxed total and grey lines for your items.',
        intro: 'An invoice format is the page a seller sends to ask for payment. These nine show a GST invoice with GSTIN, HSN code and tax columns; a freelance invoice with hours and rate; and a simple invoice with a logo placeholder. Only the headings and total are lettered; grey lines hold your details. Type a twist, such as green header, and it is a request.',
        useCases: [
            'billing a client for a service',
            'a freelance invoice billed by the hour',
            'a sale invoice for a small shop',
            'a GST invoice for a registered business',
        ],
        faq: [
            {q: 'What are GSTIN and the HSN code?', a: 'GSTIN is the tax number of a registered business. The HSN code identifies the goods on the invoice. The layout shows a line for each, to fill in.'},
            {q: 'Does the page calculate the tax for me?', a: 'No. It is a layout with blank figures. The arithmetic is yours.'},
            {q: 'What does the freelance invoice show?', a: 'Hours and an hourly rate, so the amount is hours multiplied by the rate, set out line by line.'},
            {q: 'Can I add a stamp?', a: 'Type it as a twist, such as with stamp. It is a request for that look, not a promised result.'},
        ],
    },
}
